import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Listing, AssistantResponse } from '../types';
import { queryAssistant } from '../services/api';
import StarRating from '../components/StarRating';

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  suggestions?: Listing[];
  intent?: string;
}

const quickSuggestions = [
  'Rehabs in Florida',
  'Accepts Blue Cross',
  'Women-only programs',
  'Luxury detox in California',
];

const AssistantPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSubmit = async (query?: string) => {
    const text = (query || input).trim();
    if (!text || loading) return;

    const userMessage: ChatMessage = { role: 'user', text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response: AssistantResponse = await queryAssistant(text);
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        text: response.message,
        suggestions: response.suggestions,
        intent: response.intent,
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Sorry, I had trouble processing that request. Please try again.',
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      maxWidth: '800px', margin: '0 auto', padding: '24px 20px',
      display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)',
    }}>
      <h1 style={{ fontSize: '24px', margin: '0 0 4px' }}>🤖 AI Search Assistant</h1>
      <p style={{ color: '#666', margin: '0 0 16px', fontSize: '14px' }}>
        Ask me anything about rehab centers and treatment options
      </p>

      {/* Quick Suggestions */}
      {messages.length === 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
          {quickSuggestions.map(suggestion => (
            <button key={suggestion} onClick={() => handleSubmit(suggestion)} style={{
              padding: '8px 16px', background: '#e3f2fd', color: '#1565c0',
              border: '1px solid #bbdefb', borderRadius: '20px', fontSize: '13px',
              fontWeight: 600, cursor: 'pointer',
            }}>
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Chat Messages */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '16px 0',
        display: 'flex', flexDirection: 'column', gap: '16px',
      }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#aaa' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>💬</div>
            <p style={{ fontSize: '16px' }}>Start a conversation by typing a question below</p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} style={{
            display: 'flex', flexDirection: 'column',
            alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
            maxWidth: '85%',
            alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
          }}>
            {/* Intent label */}
            {msg.intent && (
              <span style={{
                fontSize: '11px', color: '#999', marginBottom: '4px',
                fontStyle: 'italic',
              }}>
                Detected: {msg.intent}
              </span>
            )}

            {/* Message bubble */}
            <div style={{
              padding: '12px 16px', borderRadius: '16px', fontSize: '14px',
              lineHeight: 1.6, maxWidth: '100%',
              ...(msg.role === 'user'
                ? { background: '#e3f2fd', color: '#1a237e', borderBottomRightRadius: '4px' }
                : { background: '#f5f5f5', color: '#333', borderBottomLeftRadius: '4px' }),
            }}>
              {msg.text}
            </div>

            {/* Suggestion Cards */}
            {msg.suggestions && msg.suggestions.length > 0 && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '10px', marginTop: '12px', width: '100%',
              }}>
                {msg.suggestions.map(listing => (
                  <MiniListingCard key={listing.listingId} listing={listing} />
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Loading indicator */}
        {loading && (
          <div style={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
            <div style={{
              padding: '12px 16px', background: '#f5f5f5', borderRadius: '16px',
              borderBottomLeftRadius: '4px', fontSize: '14px', color: '#888',
            }}>
              <span style={{ display: 'inline-block', animation: 'none' }}>Thinking...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{
        display: 'flex', gap: '8px', padding: '16px 0 0',
        borderTop: '1px solid #e0e0e0',
      }}>
        <input
          style={{
            flex: 1, padding: '12px 16px', border: '1px solid #e0e0e0',
            borderRadius: '24px', fontSize: '14px', outline: 'none',
          }}
          placeholder="Ask about rehabs, treatments, insurance..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
          disabled={loading}
        />
        <button onClick={() => handleSubmit()} disabled={loading || !input.trim()} style={{
          padding: '12px 24px', background: loading || !input.trim() ? '#9fa8da' : '#1a237e',
          color: '#fff', border: 'none', borderRadius: '24px', fontSize: '14px',
          fontWeight: 700, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
        }}>
          Send
        </button>
      </div>
    </div>
  );
};

const MiniListingCard: React.FC<{ listing: Listing }> = ({ listing }) => {
  const rating = listing.ratings?.overall?.value || 0;
  return (
    <Link to={`/listing/${listing.listingId}`} style={{
      display: 'block', padding: '12px', background: '#fff', borderRadius: '10px',
      border: '1px solid #e0e0e0', textDecoration: 'none', color: '#333',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s',
    }}>
      <div style={{ fontWeight: 700, fontSize: '14px', color: '#1a237e', marginBottom: '4px' }}>
        {listing.name}
      </div>
      {listing.address && (
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
          📍 {listing.address.city}, {listing.address.region}
        </div>
      )}
      <StarRating rating={rating} size="sm" />
      {listing.treatmentTypes && listing.treatmentTypes.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '6px' }}>
          {listing.treatmentTypes.slice(0, 2).map((t, i) => (
            <span key={i} style={{
              background: '#e3f2fd', color: '#1565c0', padding: '1px 6px',
              borderRadius: '8px', fontSize: '10px',
            }}>{t}</span>
          ))}
        </div>
      )}
    </Link>
  );
};

export default AssistantPage;
